import React, { useCallback, useEffect, useRef } from 'react';
import { addMinutes, endOfDay, format, isAfter, parseISO, startOfDay, startOfWeek } from 'date-fns';
import { calculateEventPositions, getHoursRange, WCS } from './week-utils';
import {
  IAddConfig,
  IAddEvent,
  ICalendarEvent,
  IMarkedEvents,
  IMenuRenderInfo,
  IRenderInfo,
  ISnapConfig,
  ObservedSize,
  TEventType,
  TWeekStart,
  WeekCalendarHandle,
} from '../calendars';
import BackgroundGrid from './components/background-grid';
import EventMenu from './components/event-menu';
import useResizeObserver from 'use-resize-observer';
import WeekHeader from './components/week-header';
import Week from './components/week';
import { CalendarWeekSC } from './calendar-week.styled';
import getDaysArray from '../../../../lib/utils/get-days-array';
import { TDay } from '../../../../types/e-visits';
import WeekDayItem from './components/week-day/week-day-item';
import AddItem from './components/add-item';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../store/ducks/reservation-form-slice';
import { TUserMedicalInstitution } from '../../../../types/users';
import TStore from '../../../../types/store';

interface CalendarWeekProps<T> {
  /**
   * Date of week day to display
   */
  selectedDate: string;
  /**
   * Events to display
   */
  events?: ICalendarEvent<T>[];
  /**
   * Display colored ranges in background of events
   */
  markedEvents?: IMarkedEvents;
  /**
   * Render item on timeline
   */
  renderEvent: (info: IRenderInfo<T>) => React.ReactElement;
  /**
   * Render menu after click
   */
  renderMenu: (info: IMenuRenderInfo<T>) => React.ReactElement;
  /**
   * Calendar show whole 24 hours, or calculate min max values + hoursPadding
   */
  displayAllDay?: boolean;
  /**
   * Number of hours to add to each end of range
   */
  hoursPadding?: number;
  /**
   * On change callback after week change
   */
  onChange?: (newDate: Date) => void;
  /**
   * Function to determine if we can select item
   */
  onMenuOpen?: (event: ICalendarEvent<T>, elementRef: React.RefObject<HTMLDivElement>) => boolean;
  /**
   * Function om menu close, if false returned, menu wont close
   */
  onMenuClose?: (event: ICalendarEvent<T>) => boolean;
  /**
   * Show bar representing current time on calendar
   */
  showDayProgress?: boolean;
  /**
   * Day of week to show first
   */
  weekStartsOn?: TWeekStart;
  /**
   * Render for add event
   */
  renderAddEvent?: (event: ICalendarEvent<any>) => React.ReactElement;
  /**
   * Callback on interactive change
   */
  onEditItemChange?: (original: ICalendarEvent<any>, modified: ICalendarEvent<any>) => void;
  /**
   * Width of menu
   */
  menuWidth?: number;
  /**
   * config to enable menu snapping
   */
  snapMenu?: ISnapConfig;
  /**
   * Limit height of calendar to scroll inside
   * Use number or css for height
   */
  calendarMaxHeight?: number | string;
  /**
   * Blur clendar on loading
   */
  loading?: boolean;

  selectedInstitution?: TUserMedicalInstitution | undefined;

  /**
   * Which mode is currently used
   */
  mode: 'edit' | 'view';
  disableAdd?: boolean;
}

interface IMenuState {
  visible: boolean;
  // position?: ReturnType<typeof getBoundingClientRectObj>;
  data?: any;
  currentRef?: React.RefObject<HTMLDivElement>;
  originalRef?: React.RefObject<HTMLDivElement>;
  type?: TEventType;
  index?: number;
}

const CalendarWeek = <T extends any>(
  {
    selectedDate,
    events,
    markedEvents,
    renderEvent,
    renderMenu,
    onChange,
    displayAllDay,
    hoursPadding = 1,
    onMenuOpen,
    onMenuClose,
    showDayProgress,
    weekStartsOn = 1,
    renderAddEvent,
    onEditItemChange,
    menuWidth = WCS.menuWidth,
    snapMenu,
    calendarMaxHeight,
    loading,
    selectedInstitution,
    mode,
    disableAdd,
  }: CalendarWeekProps<T>,
  ref?: React.Ref<WeekCalendarHandle<T>>,
) => {
  const weekStart = startOfDay(startOfWeek(new Date(selectedDate), { weekStartsOn }));
  const [eventMenu, setEventMenu] = React.useState<IMenuState>({ visible: false });
  const [addEvent, setAddEvent] = React.useState<IAddEvent | undefined>(undefined);
  const [dims, setDims] = React.useState<ObservedSize | undefined>(undefined);
  const hoursRanges = React.useMemo(() => {
    if (displayAllDay) {
      return { minHour: 0, maxHour: 24, hoursLength: 24 };
    }

    const { min, max } = getHoursRange(events, markedEvents);

    const minHour = min ? Math.max(min - hoursPadding, 0) : 0;
    const maxHour = max ? Math.min(max + hoursPadding, 24) : 24;

    return {
      minHour,
      maxHour,
      hoursLength: Math.abs(maxHour - minHour),
    };
  }, [displayAllDay, events, markedEvents, hoursPadding]);

  const dispatch = useDispatch();

  const handleClick = React.useCallback(
    (
      nodeRef: React.RefObject<HTMLDivElement>,
      event: ICalendarEvent,
      eventType: TEventType,
      index?: number,
    ) => {
      setEventMenu((o) => {
        /** handle click on same element */
        if (
          o.visible &&
          ((o.data === event && !onMenuClose) || (onMenuClose && onMenuClose(o.data!)))
        ) {
          setAddEvent(undefined);
          return { ...o, visible: false, originalRef: nodeRef };
        }

        /** handle open menu */
        if (!o.visible && !(!onMenuOpen || (onMenuOpen && onMenuOpen?.(event, nodeRef)))) {
          return { ...o, visible: false, originalRef: nodeRef };
        }

        dispatch(
          actions.setECalendarEvent({
            ...event.eventData.event,
          }),
        );

        return {
          visible: true,
          data: event,
          currentRef: nodeRef,
          originalRef: nodeRef,
          type: eventType,
          index,
        };
      });
    },
    [onMenuClose, onMenuOpen],
  );

  const updateMenuPos = React.useCallback(
    (
      nodeRef: React.RefObject<HTMLDivElement>,
      event: ICalendarEvent,
      eventType: TEventType,
      index?: number,
    ) => {
      setEventMenu((o) => {
        return {
          ...o,
          visible: true,
          data: event,
          currentRef: nodeRef,
          type: eventType,
          index,
        };
      });
    },
    [],
  );

  const handleMenuClose = React.useCallback(
    (force?: boolean) => {
      setEventMenu((o) => {
        if (!onMenuClose || (onMenuClose && onMenuClose(o.data!)) || force) {
          dispatch(actions.clearEvent());
          setAddEvent(undefined);
          if (o.originalRef?.current) {
            o.originalRef.current.style.opacity = '1';
          }

          return {
            ...o,
            visible: false,
          };
        }

        return o;
      });
    },
    [onMenuClose, dispatch],
  );

  const { selectedTag } = useSelector((state: TStore) => ({
    selectedTag: state.eVisits.selectedTag,
  }));

  const addEventHandler = React.useCallback(
    ({ start, end, eventData, minTime, maxTime, enableGestures }: IAddConfig<T>) => {
      setAddEvent((o) => {
        const minT = minTime === null ? undefined : minTime;
        const maxT = maxTime === null ? undefined : maxTime;

        const _eventData = Object.assign({}, eventData);
        // @ts-ignore
        if (_eventData?.tag && !selectedTag && !eventData.id) {
          // @ts-ignore
          _eventData.tag = undefined;
        }

        const obj = {
          start,
          end,
          eventData: _eventData,
          minTime: minT,
          maxTime: maxT,
        };

        return {
          original: {
            ...obj,
          },
          copy: {
            ...obj,
          },
          isResizing: false,
          enableGestures: enableGestures ?? true,
        };
      });
    },
    [selectedTag],
  );

  const onExited = React.useCallback(() => {
    setEventMenu({
      visible: false,
    });
  }, []);

  const handleEditStart = React.useCallback(
    (nodeRef: React.RefObject<HTMLDivElement>, config: IAddConfig<T>) => {
      if (nodeRef?.current) {
        nodeRef.current.style.opacity = '0';
      }

      handleClick(nodeRef, config, 'addEvent', config.index);
      addEventHandler(config);
    },
    [addEventHandler, handleClick],
  );

  const calendarWrapperRef = React.useRef<HTMLDivElement>(null);
  const calendarEventsRef = React.useRef<HTMLDivElement>(null);
  useResizeObserver({
    onResize: setDims,
    ref: calendarWrapperRef,
  });

  React.useImperativeHandle(
    ref,
    () => ({
      closeMenu: handleMenuClose,
      addEvent: addEventHandler,
    }),
    [handleMenuClose, addEventHandler],
  );

  const weekStartDate = format(weekStart, 'yyyy-MM-dd');
  const calendarHeight = hoursRanges.hoursLength * WCS.cellHeight + WCS.dayHeaderHeight;

  const positions = React.useMemo(() => {
    return events
      ? calculateEventPositions(hoursRanges.minHour, events, WCS.dayHeaderHeight + WCS.gridPadding)
      : [];
  }, [events, hoursRanges.minHour]);

  const weekDays = React.useMemo(() => getDaysArray<TDay>(weekStartsOn, 'number'), [weekStartsOn]);
  const timeLineWidth = dims?.width ? (dims.width - 2 * WCS.timeLineWidth) / 7 : 0;

  const Events = React.useMemo(() => {
    return positions.map((item, index) => {
      return (
        <WeekDayItem
          key={`${item.event.start}-${item.event.end}-${index}`}
          onClick={handleClick}
          index={index}
          item={item}
          renderEvent={renderEvent}
          weekDays={weekDays}
          timeLineWidth={timeLineWidth}
          onEdit={handleEditStart}
        />
      );
    });
  }, [positions, weekDays, timeLineWidth, handleClick, renderEvent, handleEditStart]);

  const handleWeekDayClick = useCallback(
    (e: any, weekDayRect: any, date: any) => {
      if (disableAdd) {
        return;
      }
      function calculateTimeOffset(e: React.MouseEvent) {
        const rect = e.currentTarget.getBoundingClientRect();
        const minutes = (e.clientY - (rect.top + WCS.dayHeaderHeight)) / WCS.minuteHeight;
        return Math.floor(minutes / WCS.minSelectInterval) * WCS.minSelectInterval;
      }

      const timeOffset = calculateTimeOffset(e);
      const startDate = addMinutes(parseISO(date), timeOffset);
      const endDate = addMinutes(startDate, 15);
      const isInFuture = isAfter(startDate, Date.now());

      if (isInFuture) {
        const start = startDate.toISOString();
        const end = endDate.toISOString();
        dispatch(
          actions.setECalendarEvent({
            start,
            end,
            eventData: {
              medicalInstitutionId: selectedInstitution?.medicalInstitution?.id,
              expertiseCode: selectedInstitution?.expertise?.code,
              toTime: end,
              fromTime: start,
              blockSize: 15,
            },
          }),
        );

        addEventHandler({
          start,
          end,
          minTime: startOfDay(new Date(start)).toISOString(),
          maxTime: endOfDay(new Date(start)).toISOString(),
          eventData: {} as T,
        });
      }
    },
    [selectedInstitution, eventMenu.visible, disableAdd],
  );

  useEffect(() => {
    if (eventMenu.data?.end && eventMenu.data?.start) {
      dispatch(
        actions.updateCalendarEventTimes({
          start: eventMenu.data.start,
          end: eventMenu.data.end,
        }),
      );
    }
  }, [eventMenu.data?.end, eventMenu.data?.start]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const setHourY = useCallback(
    (timeY: number) => {
      if (wrapperRef.current) {
        wrapperRef.current?.scrollTo({
          top: timeY - wrapperRef.current.getBoundingClientRect().y - WCS.cellHeight,
          behavior: 'smooth',
        });
      }
    },
    [wrapperRef],
  );

  return (
    <CalendarWeekSC
      style={{
        height: calendarHeight + WCS.weekHeaderHeight + WCS.gridPadding,
        maxHeight: calendarMaxHeight,
      }}
      ref={calendarWrapperRef}
      className={eventMenu.visible ? 'menu-open' : ''}
      mode={mode}
    >
      <WeekHeader
        date={selectedDate}
        onChange={onChange}
        weekStartsOn={weekStartsOn}
        loading={loading}
      />
      <div
        className="content-wrapper"
        style={{
          position: 'relative',
          overflowY: 'auto',
          height: calendarMaxHeight
            ? `calc(100% - ${WCS.weekHeaderHeight + WCS.gridPadding}px)`
            : '100%',
          filter: loading ? 'blur(2px)' : '',
        }}
        ref={calendarEventsRef}
      >
        <div className="calendar-content-wrapper" ref={wrapperRef}>
          <BackgroundGrid
            minHour={hoursRanges.minHour}
            maxHour={hoursRanges.maxHour}
            style={styles.grid}
            showDayProgress={showDayProgress}
            calendarDims={dims}
            weekStartDate={weekStartDate}
            weekStartsOn={weekStartsOn}
            setHourY={setHourY}
          />
          <div
            className="calendar-content"
            style={{
              height: calendarHeight,
            }}
          >
            <Week
              weekStart={weekStartDate}
              minHour={hoursRanges.minHour}
              markedEvents={markedEvents}
              weekStartsOn={weekStartsOn}
              onClick={handleWeekDayClick}
            />
            {Events}
            <AddItem
              addEvent={addEvent}
              setAddEvent={setAddEvent}
              weekDays={weekDays}
              minHour={hoursRanges.minHour}
              timeLineWidth={timeLineWidth}
              renderAddEvent={renderAddEvent}
              onEditItemChange={onEditItemChange}
              updateMenuPosition={updateMenuPos}
            />
          </div>
        </div>
      </div>
      <EventMenu
        visible={eventMenu.visible}
        onClose={handleMenuClose}
        onExited={onExited}
        renderMenu={renderMenu}
        event={eventMenu.data}
        eventType={eventMenu.type}
        currentElementRef={eventMenu.currentRef}
        menuWidth={menuWidth}
        onEdit={addEventHandler}
        timeLineWidth={timeLineWidth}
        snapMenu={snapMenu}
        calendarEventsRef={calendarEventsRef}
        calendarWrapperRef={calendarMaxHeight ? calendarWrapperRef : undefined}
      />
    </CalendarWeekSC>
  );
};

const styles = {
  grid: {
    top: WCS.dayHeaderHeight,
    right: WCS.timeLineWidth,
  },
} as const;

export default React.memo(React.forwardRef(CalendarWeek)) as <T extends any>(
  props: CalendarWeekProps<T> & React.RefAttributes<WeekCalendarHandle<T>>,
) => JSX.Element;
